'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { splitSentences } from '@/lib/tts/splitSentences';
import { stripMarkdownForSpeech } from '@/lib/tts/stripMarkdownForSpeech';
import { synthesizeSentence, voiceIdForLanguage, TtsRequestError } from '@/lib/tts/client';
import { useLanguage } from '@/lib/i18n';
import { useTtsPlaybackStore } from '@/stores/ttsPlaybackStore';

export type TtsStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/** 当前句之外，往后预取多少句音频 */
const PREFETCH_AHEAD = 3;

/** 失败原因：区分出余额不足 / 服务不可用，让 UI 能给出可操作的提示 */
export type TtsErrorKind = 'generic' | 'insufficient-credits' | 'unavailable';

export interface UseTextToSpeechOptions {
  /** 内容 id（一般是消息 id），用于全局播放互斥 */
  id: string;
  /** 原始 Markdown 正文，内部会清洗并分句 */
  audioSources: string[] | string;
  /** 业务音色 ID（见 lib/providers/tts/voices.ts）；不传则按当前界面语言选 */
  voice?: string;
  /** 用量上报归因，透传给 /api/tts */
  chatId?: string;
  messageId?: string;
  /** 初始倍速，1 为正常速度 */
  initialPlaybackRate?: number;
}

export interface UseTextToSpeechReturn {
  status: TtsStatus;
  isLoading: boolean;
  isSpeaking: boolean;
  isPaused: boolean;
  /** 有没有可朗读的内容 */
  canSpeak: boolean;
  error: string | null;
  errorKind: TtsErrorKind | null;
  sentences: string[];
  currentIndex: number;
  playbackRate: number;
  speak: (startIndex?: number) => void;
  pause: () => void;
  resume: () => void;
  /** 播放/暂停一键切换，按钮的默认行为 */
  toggle: () => void;
  stop: () => void;
  next: () => void;
  previous: () => void;
  setPlaybackRate: (rate: number) => void;
}

/** /api/tts 的状态码 → UI 能区分对待的失败类型（见 docs/prd/tts-provider-adapter.md） */
function classifyTtsError(err: unknown): TtsErrorKind {
  if (err instanceof TtsRequestError) {
    if (err.isInsufficientCredits) return 'insufficient-credits';
    if (err.isServiceUnavailable) return 'unavailable';
  }
  return 'generic';
}

/**
 * 分句流水线朗读引擎（Web）。
 *
 * 一句一次请求：播当前句的同时预取下一句，首句几百毫秒就能出声，
 * 不用等整篇合成完。音频用一个复用的 HTMLAudioElement 播放——
 * 复用同一个元素能保住首次点击拿到的自动播放授权。
 */
export function useTextToSpeech({
  id,
  audioSources,
  voice,
  chatId,
  messageId,
  initialPlaybackRate = 1,
}: UseTextToSpeechOptions): UseTextToSpeechReturn {
  const [status, setStatus] = useState<TtsStatus>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<TtsErrorKind | null>(null);
  const [playbackRate, setPlaybackRateState] = useState(initialPlaybackRate);

  // 没显式指定音色时跟随界面语言：中文界面用中文音色，英文界面用英文音色。
  // 供应商音色名对客户端不可见，这里只认业务音色 ID。
  const { currentLanguage } = useLanguage();
  const resolvedVoice = voice ?? voiceIdForLanguage(currentLanguage?.code);

  // 分句在渲染期派生，不用 effect 同步（内容没变时 useMemo 直接复用）
  const sentences = useMemo(() => {
    const sources = Array.isArray(audioSources) ? audioSources : [audioSources];
    return sources.flatMap((source) => splitSentences(stripMarkdownForSpeech(source)));
  }, [audioSources]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sentencesRef = useRef(sentences);
  const bufferRef = useRef(new Map<number, Promise<Blob>>());
  const objectUrlsRef = useRef(new Set<string>());
  const abortRef = useRef<AbortController | null>(null);
  /** 每次 stop/speak 自增，用于丢弃过期的异步结果 */
  const generationRef = useRef(0);
  /** 用户是否希望在播——避免"加载完成后自动播放"覆盖掉用户的暂停意图 */
  const userWantsPlayingRef = useRef(false);
  const playbackRateRef = useRef(initialPlaybackRate);
  const indexRef = useRef(0);

  const acquire = useTtsPlaybackStore((state) => state.acquire);
  const release = useTtsPlaybackStore((state) => state.release);
  // 只订阅"是否被别人抢占"这个派生布尔量，避免每次 activeId 变动都重渲染
  const isPreempted = useTtsPlaybackStore(
    (state) => state.activeId !== null && state.activeId !== id,
  );

  useEffect(() => {
    sentencesRef.current = sentences;
  }, [sentences]);

  const revokeObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
  }, []);

  const teardown = useCallback(() => {
    generationRef.current += 1;
    userWantsPlayingRef.current = false;
    abortRef.current?.abort();
    abortRef.current = null;
    bufferRef.current.clear();

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    revokeObjectUrls();
  }, [revokeObjectUrls]);

  const stop = useCallback(() => {
    teardown();
    indexRef.current = 0;
    setCurrentIndex(0);
    setStatus('idle');
    release(id);
  }, [id, release, teardown]);

  /** 取某一句的音频，同一句并发只会发一次请求 */
  const ensureAudio = useCallback(
    (index: number): Promise<Blob> => {
      const cached = bufferRef.current.get(index);
      if (cached) return cached;

      const sentence = sentencesRef.current[index];
      if (!sentence) {
        return Promise.reject(new Error('Sentence out of range'));
      }

      if (!abortRef.current) {
        abortRef.current = new AbortController();
      }

      const pending = synthesizeSentence(sentence, {
        voice: resolvedVoice,
        chatId,
        messageId,
        signal: abortRef.current.signal,
      }).catch((err) => {
        // 失败的句子从缓冲里摘掉，下次还能重试
        bufferRef.current.delete(index);
        throw err;
      });

      bufferRef.current.set(index, pending);
      return pending;
    },
    [chatId, messageId, resolvedVoice],
  );

  /**
   * 往后预取 PREFETCH_AHEAD 句。
   *
   * 短句朗读只要几秒，单句预取顶不住一次合成的耗时（Gemini 实测 3s+），
   * 句与句之间就会卡顿；多备几句把这段延迟藏进播放时间里。
   * 仍然是有上限的滑动窗口——暂停期间不会无限往下拉，浪费调用次数。
   */
  const prefetchAhead = useCallback(
    (index: number) => {
      const total = sentencesRef.current.length;
      for (let offset = 1; offset <= PREFETCH_AHEAD; offset++) {
        const target = index + offset;
        if (target >= total) return;
        void ensureAudio(target).catch(() => {
          // 预取失败不影响当前播放，真正轮到它时会重试
        });
      }
    },
    [ensureAudio],
  );

  const getAudioElement = useCallback((): HTMLAudioElement | null => {
    if (typeof window === 'undefined') return null;
    if (!audioRef.current) {
      const audio = new Audio();
      audio.preload = 'auto';
      audioRef.current = audio;
    }
    return audioRef.current;
  }, []);

  const playIndex = useCallback(
    async (index: number) => {
      const audio = getAudioElement();
      if (!audio) return;

      const list = sentencesRef.current;
      if (index < 0 || index >= list.length) {
        stop();
        return;
      }

      const generation = generationRef.current;
      indexRef.current = index;
      setCurrentIndex(index);
      setError(null);
      setErrorKind(null);

      const buffered = bufferRef.current.has(index);
      if (!buffered) {
        setStatus('loading');
      }

      try {
        const blob = await ensureAudio(index);
        // 期间被 stop / 重新 speak 了，丢弃这次结果
        if (generation !== generationRef.current) return;
        // 加载过程中用户点了暂停：保持暂停，不自动开播
        if (!userWantsPlayingRef.current) {
          setStatus('paused');
          return;
        }

        const url = URL.createObjectURL(blob);
        objectUrlsRef.current.add(url);
        audio.src = url;
        audio.playbackRate = playbackRateRef.current;
        await audio.play();

        if (generation !== generationRef.current) return;
        setStatus('playing');
        prefetchAhead(index);
      } catch (err) {
        if (generation !== generationRef.current) return;
        // 主动中断（切换/卸载）不算错误
        if (err instanceof Error && err.name === 'AbortError') return;

        userWantsPlayingRef.current = false;
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Text to speech failed');
        setErrorKind(classifyTtsError(err));
        release(id);
      }
    },
    [ensureAudio, getAudioElement, id, prefetchAhead, release, stop],
  );

  const speak = useCallback(
    (startIndex = 0) => {
      if (sentencesRef.current.length === 0) return;

      // 重新开播：作废上一轮的在途请求，但保留已缓存的音频（在 Cache Storage 里）
      teardown();
      acquire(id);
      userWantsPlayingRef.current = true;
      void playIndex(Math.max(0, Math.min(startIndex, sentencesRef.current.length - 1)));
    },
    [acquire, id, playIndex, teardown],
  );

  const pause = useCallback(() => {
    userWantsPlayingRef.current = false;
    audioRef.current?.pause();
    setStatus((current) => (current === 'loading' || current === 'playing' ? 'paused' : current));
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    userWantsPlayingRef.current = true;
    acquire(id);

    // 当前句已经加载好：直接续播，位置保留
    if (audio?.src && audio.paused && audio.currentTime > 0) {
      audio.playbackRate = playbackRateRef.current;
      void audio
        .play()
        .then(() => setStatus('playing'))
        .catch((err) => {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Playback failed');
          setErrorKind('generic');
        });
      return;
    }

    void playIndex(indexRef.current);
  }, [acquire, id, playIndex]);

  const toggle = useCallback(() => {
    if (status === 'playing' || status === 'loading') {
      pause();
      return;
    }
    if (status === 'paused') {
      resume();
      return;
    }
    speak(0);
  }, [pause, resume, speak, status]);

  const next = useCallback(() => {
    const target = indexRef.current + 1;
    if (target >= sentencesRef.current.length) {
      stop();
      return;
    }
    userWantsPlayingRef.current = true;
    void playIndex(target);
  }, [playIndex, stop]);

  const previous = useCallback(() => {
    userWantsPlayingRef.current = true;
    void playIndex(Math.max(0, indexRef.current - 1));
  }, [playIndex]);

  const setPlaybackRate = useCallback((rate: number) => {
    const clamped = Math.min(3, Math.max(0.5, rate));
    playbackRateRef.current = clamped;
    setPlaybackRateState(clamped);
    if (audioRef.current) {
      audioRef.current.playbackRate = clamped;
    }
  }, []);

  // 一句播完自动接下一句
  useEffect(() => {
    const audio = getAudioElement();
    if (!audio) return;

    const handleEnded = () => {
      const nextIndex = indexRef.current + 1;
      if (nextIndex >= sentencesRef.current.length) {
        stop();
        return;
      }
      if (!userWantsPlayingRef.current) return;
      void playIndex(nextIndex);
    };

    const handleError = () => {
      if (!userWantsPlayingRef.current) return;
      setStatus('error');
      setError('Audio playback failed');
      setErrorKind('generic');
      release(id);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [getAudioElement, id, playIndex, release, stop]);

  // 被别处的朗读抢占：立即停止自己
  useEffect(() => {
    if (isPreempted && userWantsPlayingRef.current) {
      teardown();
      indexRef.current = 0;
      setCurrentIndex(0);
      setStatus('idle');
    }
  }, [isPreempted, teardown]);

  // 正文变化（例如流式续写或切换版本）时中断朗读，避免读到对不上的句子。
  // audioSources 传数组时父组件每次给的都是新引用，所以比内容而不是比引用
  const contentKey = useMemo(() => sentences.join('\n'), [sentences]);
  const contentKeyRef = useRef(contentKey);
  useEffect(() => {
    if (contentKeyRef.current === contentKey) return;
    contentKeyRef.current = contentKey;
    if (userWantsPlayingRef.current || status !== 'idle') {
      stop();
    }
  }, [contentKey, status, stop]);

  useEffect(
    () => () => {
      teardown();
      release(id);
      audioRef.current = null;
    },
    [id, release, teardown],
  );

  return {
    status,
    isLoading: status === 'loading',
    isSpeaking: status === 'playing',
    isPaused: status === 'paused',
    canSpeak: sentences.length > 0,
    error,
    errorKind,
    sentences,
    currentIndex,
    playbackRate,
    speak,
    pause,
    resume,
    toggle,
    stop,
    next,
    previous,
    setPlaybackRate,
  };
}
